import { type MediaControls, type MediaEvent } from '.'
import type jwplayer from 'jwplayer'

const paramEventMap: { [k in MediaEvent]?: keyof jwplayer.EventParams } = {
  timeupdate: 'time'
} as const

const noParamEventMap: { [k in MediaEvent]?: jwplayer.NoParamEvent } = {
  seeked: 'seeked'
} as const

export class JwPlayerMediaControls implements MediaControls {
  mediaEl: jwplayer.JWPlayer
  constructor (mediaEl: HTMLElement) {
    if (window.jwplayer == null) throw new Error('JwPlayerMediaControls requires window.jwplayer to be loaded')
    this.mediaEl = window.jwplayer(mediaEl as Element)
  }

  get currentTime (): number {
    return this.mediaEl.getPosition()
  }

  get paused (): boolean {
    return this.mediaEl.getState() !== 'playing'
  }

  get duration (): number {
    return this.mediaEl.getDuration()
  }

  get currentSrc (): string | undefined {
    return this.mediaEl.getPlaylistItem()?.file
  }

  on (ev: MediaEvent, handler: (data: any) => void): JwPlayerMediaControls {
    const jwEv = paramEventMap[ev]
    if (jwEv != null) {
      this.mediaEl.on(jwEv, handler)
    } else {
      const jwEv = noParamEventMap[ev]
      jwEv != null && this.mediaEl.on(jwEv, handler as () => void)
    }
    return this
  }

  off (ev: MediaEvent, handler: (data: any) => void): JwPlayerMediaControls {
    const jwEv = paramEventMap[ev]
    if (jwEv != null) {
      this.mediaEl.off(jwEv, handler)
    } else {
      const jwEv = noParamEventMap[ev]
      jwEv != null && this.mediaEl.off(jwEv, handler as () => void)
    }
    return this
  }

  play () {
    this.mediaEl.play()
  }

  pause () {
    this.mediaEl.pause()
  }

  setCurrentTime (seconds: number) {
    this.mediaEl.seek(seconds)
  }

  mute (muted: boolean) {
    this.mediaEl.setMute(muted)
  }

  setVolume (value: number) {
    this.mediaEl.setVolume(value)
  }

  getVolume (): number {
    return this.mediaEl.getVolume()
  }
}
